import http, { syncAppCodeHeader } from '@/shared/services/http.config'
import {
  clearAllAuthStorage,
  clearPendingContextSelection as clearPendingContextSelectionStorage,
  clearActiveContext as clearActiveContextStorage,
  getActiveContext as getActiveContextStorage,
  setActiveContext as setActiveContextStorage,
  setAuthToken as setAuthTokenStorage,
  removeMany,
  setContexts,
  setCustomer,
  getCustomer,
  setProfileLastSynced,
} from "@/modules/auth/services/auth.storage";

export const AUTH_TOKEN_KEY = 'auth_token'
export const AUTH_PENDING_CONTEXT_SELECTION_KEY = 'auth_requires_context_selection'
export const AUTH_CONTEXTS_KEY = 'auth_contexts'
export const AUTH_PROFILE_SYNC_KEY = 'auth_profile_last_synced'
export const AUTH_SELECTION_TOKEN_KEY = 'auth_selection_token'
export const AUTH_CUSTOMER_KEY = 'auth_customer'
export const AUTH_ACTIVE_CONTEXT_KEY = 'auth_active_context'

export type ProfileAddress = {
  add1: string
  city: string
  state: string
  zip: string
}

export type StripeProfile = {
  default_payment_method_id: string
  stripe_customer_id: string
  name: string
  email: string
  phone: string
}

export type ProfileVendor = {
  id: number
  name: string
  code: string
}

// Some `/me` responses include a nested `client` object with customer fields.
export type ProfileClient = {
  id?: number
  first_name?: string
  last_name?: string
  fname?: string
  lname?: string
  name?: string
  full_name?: string
  email?: string
  phone?: string
  avatar?: string
  [key: string]: any
}

export type ProfileData = {
  fname: string
  lname: string
  email: string
  phone: string
  avatar: string
  address: ProfileAddress | null
  stripe_profile: StripeProfile | null
  vendor: ProfileVendor | null
  // optional extended `/me` payload fields
  client?: ProfileClient
  active_context?: LoginContext
  contexts?: LoginContext[]
  [key: string]: any
}

export type ProfileResponse = {
  data?: ProfileData
  token?: string
  access_token?: string
}

export type LoginCustomer = {
  id: number
  first_name: string
  last_name: string
  email: string
  phone?: string
  avatar?: string
}

export type LoginContext = {
  context_id: string
  key: string
  type: string
  roles: string[]
  permissions?: string[]
  company?: {
    code: string
    name: string
    logo: string
  }
  vendor?: {
    code: string
    name: string
    logo: string | null
  }
  label?: string
  [key: string]: any
}

export type LoginResponse = {
  token?: string
  access_token?: string
  token_type?: string
  requires_context_selection?: boolean
  selection_token?: string
  contexts?: LoginContext[]
  customer?: LoginCustomer
  client?: {
    id?: number
    avatar?: string
    first_name?: string
    last_name?: string
    name?: string
    email?: string
    phone?: string
    first_login?: boolean
    [key: string]: any
  }
  active_context?: LoginContext | null
}

export type ContextKind = 'customer' | 'vendor' | 'company' | ''

export function getContextKind(ctx?: Partial<LoginContext> | null): ContextKind {
  if (!ctx || typeof ctx !== 'object') return ''

  const type = (ctx.type || '').toString().trim().toLowerCase()
  if (type === 'customer' || type === 'vendor' || type === 'company') return type

  const hasVendor = Boolean(ctx.vendor || ctx.vendor_code);
  if (hasVendor) return 'vendor';

  const hasCompany = Boolean(ctx.company || ctx.company_code);
  if (hasCompany) return 'company';

  return ''
}

function hasProfileEnvelope(
  value: ProfileResponse | ProfileData
): value is ProfileResponse {
  return Boolean(value) && typeof value === 'object' && 'data' in value
}

function hasTokenFields(
  value: ProfileResponse | ProfileData
): value is Pick<ProfileResponse, 'token' | 'access_token'> {
  return Boolean(value) && typeof value === 'object' &&
    ('token' in value || 'access_token' in value)
}

function normalizeLoginResponse(body: any): LoginResponse {
    const data = body?.data?.data ?? body?.data ?? body

    // Return contexts as-is from backend, simply ensuring context_id is present for compatibility.
    const rawContexts = Array.isArray(data?.contexts) ? data.contexts : []
    const contexts: LoginContext[] = rawContexts.map((ctx: any) => ({
      ...ctx,
      context_id: ctx.key || ctx.context_id || '',
    }))

    // Convert `client` into `customer` shape expected by SelectContextPage.
    const client = data?.client as any
    const customerFromClient = (() => {
      if (!client || typeof client !== 'object') return null
      const explicitFirst = String(client.first_name || client.fname || '').trim()
      const explicitLast = String(client.last_name || client.lname || '').trim()
      const fullName = String(client.name || '').trim()
      const parts = fullName.split(/\s+/).filter(Boolean)
      const first_name = (explicitFirst || parts[0] || 'User').toString()
      const last_name = (explicitLast || parts.slice(1).join(' ')).toString()
      const id = Number(client.id || 0) || 0
      const email = String(client.email || '').trim()
      const phone = String(client.phone || '').trim()
      const avatar = String(client.avatar || '').trim()
      if (!id && !email && !first_name && !last_name && !fullName) return null
      return { id, first_name, last_name, email, phone: phone || undefined, avatar: avatar || undefined }
    })()

    const active_context = (data?.active_context ?? null) as any

    const requires_context_selection =
      Boolean(contexts.length) && (!active_context || typeof active_context !== 'object')

    return {
      ...data,
      contexts,
      customer: (data?.customer as any) || customerFromClient || undefined,
      requires_context_selection,
      active_context: active_context as any,
    } as LoginResponse
}

export const login = (payload) =>
  http.post('/login', payload).then((res) => res.data)

export const vendorLogin = (payload: Record<string, any>) =>
  http
    .post('/login', { ...payload, is_vendor: true })
    .then((res) => normalizeLoginResponse(res.data))

export const logout = () =>
  http.post('/logout').then((res) => res.data)

export const selectContext = (payload: { context_id: string, is_vendor: boolean }) =>
  http.post('/select-context', payload).then((res) => res.data)

export const switchContext = (payload: { context_id: string }) =>
  http.post('/switch-context', payload).then((res) => res.data)

export const selectVendorContext = (payload: { context_id: string }) =>
  http.post('/select-vendor-context', payload).then((res) => res.data)

export function saveActiveContext(context?: Partial<LoginContext> | null) {
  if (!context || typeof context !== 'object') return
  setActiveContextStorage(context)
  window.dispatchEvent(new Event('auth-changed'))
}

export function getActiveContext(): Partial<LoginContext> | null {
  return getActiveContextStorage()
}

export function clearActiveContext() {
  clearActiveContextStorage()
}

export function saveAuthToken(token?: string) {
  if (!token) return
  setAuthTokenStorage(token)
  http.defaults.headers.common.Authorization = `Bearer ${token}`
  // window.dispatchEvent(new Event('auth-changed'))
}

export function clearPendingContextSelection() {
  clearPendingContextSelectionStorage();
  // We explicitly keep AUTH_CONTEXTS_KEY and AUTH_CUSTOMER_KEY
  // to allow users to "Switch Workspace" later in the session.
}

/**
 * Removes all local storage related to contexts (contexts list, customer info, selection tokens, active context).
 */
export function clearContextStorage() {
  // Keep token intact; clear everything else context-related.
  // (Use clearAllAuthStorage() when you want a full logout/reset.)
  removeMany([
    AUTH_CONTEXTS_KEY,
    AUTH_CUSTOMER_KEY,
    AUTH_SELECTION_TOKEN_KEY,
    AUTH_PENDING_CONTEXT_SELECTION_KEY,
    AUTH_ACTIVE_CONTEXT_KEY,
    AUTH_PROFILE_SYNC_KEY,
  ]);
}

export const fetchProfile = (): Promise<ProfileResponse | ProfileData> =>
  http.get<ProfileResponse | ProfileData>('/me').then((res) => {
    const data = res.data
    const token =
      (hasTokenFields(data) ? data.token : undefined) ||
      res?.headers?.authorization?.replace(/^Bearer\s+/i, '')

    if (token) {
      saveAuthToken(token)
    }

    // Best-effort sync of auth-related local storage on every `/me` fetch.
    // This keeps `auth_active_context`, `auth_contexts`, `auth_customer`, and last-synced consistent.
    try {
      const meData: any = hasProfileEnvelope(data) ? (data as any).data : (data as any);

      if (meData?.active_context && typeof meData.active_context === "object") {
        setActiveContextStorage(meData.active_context);
      }

      if (Array.isArray(meData?.contexts)) {
        setContexts(meData.contexts);
      }

      // Normalize and store customer info (preserve existing `first_login` when backend omits it).
      const existingCustomer: any = getCustomer<any>() || {};
      const client: any = meData?.client || meData?.user || meData || {};
      const customerForStorage: any = {
        id: client?.id ?? client?.user_id ?? meData?.id ?? 0,
        first_name: client?.first_name ?? client?.fname ?? client?.name ?? "",
        last_name: client?.last_name ?? client?.lname ?? "",
        email: client?.email ?? "",
        phone: client?.phone ?? "",
        avatar: client?.avatar ?? "",
      };

      const firstLoginFromApi =
        typeof client?.first_login === "boolean" ? client.first_login : undefined;
      const firstLoginFromStored =
        typeof existingCustomer?.first_login === "boolean"
          ? existingCustomer.first_login
          : undefined;
      const resolvedFirstLogin =
        typeof firstLoginFromApi === "boolean" ? firstLoginFromApi : firstLoginFromStored;
      if (typeof resolvedFirstLogin === "boolean") {
        customerForStorage.first_login = resolvedFirstLogin;
      }
      // Only store if we have any meaningful identity fields.
      if (
        customerForStorage.id ||
        customerForStorage.email ||
        customerForStorage.first_name ||
        customerForStorage.last_name
      ) {
        setCustomer(customerForStorage);
      }

      setProfileLastSynced(Date.now());
      window.dispatchEvent(new Event("auth-changed"));
    } catch (_e) {
      // ignore sync failures
    }

    return data
  }).catch((error) => {
    // on auth errors clear all stored auth/context data and notify app
    try {
      clearAllAuthStorage();
    } catch (e) {
      // ignore storage errors
    } finally {
      delete http.defaults.headers.common.Authorization
      syncAppCodeHeader()
      window.dispatchEvent(new Event('auth-changed'))
    }

    return Promise.reject(error)
  })

export const fetchVendor = () =>
  http.get('/vendors/get-vendor').then((res) => res.data)

export const resetPassword = (payload) =>
  http.post('/password-reset', payload).then((res) => res.data)

export const resetPasswordOTP = (payload) =>
  http.post('/password-reset-otp', payload).then((res) => res.data)


// Password-reset OTP verification endpoint
export const passwordVerifyOTP = (payload) =>
  http.post('/password-verify-otp', payload).then((res) => res.data)

export const signup = (payload) =>
  http.post('/signup', payload).then((res) => res.data)


// General OTP send endpoint (e.g. inquiry / verification flows)
export const sendOTP = (payload) =>
  http.post('/send-otp', payload).then((res) => res.data)

// General OTP verification endpoint (e.g. signup / inquiry flows)
export const verifyOTP = (payload) =>
  http.post('/verify-otp', payload).then((res) => res.data)


export { hasProfileEnvelope }
